import React, { useEffect, useRef, useState } from 'react';
import './VictoryOverlay.css';

export interface Player {
  id: string;
  username: string;
  team?: 'bidding' | 'opposing';
  isReady: boolean;
  isAI?: boolean;
}

export interface ScoreData {
  playerId: string;
  lastRoundScore: number;
  cumulativeScore: number;
  gamesPlayed: number;
}

export interface VictoryOverlayProps {
  isVisible: boolean;
  winnerTeam: 'bidding' | 'opposing';
  scores: {
    biddingTeam: number;
    opposingTeam: number;
  };
  players: Player[];
  roomScores: Record<string, ScoreData> | null;
  currentPlayerId: string | null;
  bidAmount?: number;  // The winning bid amount
  bidderId?: string;   // The player who made the winning bid
  onToggleReady?: () => void;
  onStartNewGame?: () => void;
  onReturnToLobby?: () => void;
}

const VictoryOverlay: React.FC<VictoryOverlayProps> = ({
  isVisible,
  winnerTeam,
  scores,
  players,
  roomScores,
  currentPlayerId,
  bidAmount,
  bidderId,
  onToggleReady,
  onStartNewGame,
  onReturnToLobby,
}) => {
  // ALL HOOKS MUST BE CALLED BEFORE ANY CONDITIONAL RETURNS (Rules of Hooks)
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [showConfetti] = useState(true);

  // Safety checks for required props - AFTER all hooks
  const hasValidScores = scores && typeof scores.biddingTeam !== 'undefined' && typeof scores.opposingTeam !== 'undefined';
  const hasValidPlayers = players && players.length > 0;

  const winnerIsBidding = winnerTeam === 'bidding';
  const currentPlayer = hasValidPlayers ? players.find(p => p.id === currentPlayerId) : undefined;

  useEffect(() => {
    if (!isVisible || !showConfetti) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size to match viewport
    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      canvas.width = Math.floor(window.innerWidth * dpr);
      canvas.height = Math.floor(window.innerHeight * dpr);
      canvas.style.width = `${window.innerWidth}px`;
      canvas.style.height = `${window.innerHeight}px`;
      ctx.scale(dpr, dpr);
    };
    resize();
    window.addEventListener('resize', resize);

    // Confetti particles
    const particles: Array<{ x: number; y: number; vx: number; vy: number; color: string; size: number }> = [];
    const colors = ['#ff6b6b', '#ffd93d', '#6bcf7f', '#4d96ff', '#9775fa', '#ff8cc8'];
    
    for (let i = 0; i < 80; i++) {
      particles.push({
        x: Math.random() * window.innerWidth,
        y: Math.random() * window.innerHeight - window.innerHeight,
        vx: (Math.random() - 0.5) * 3,
        vy: Math.random() * 3 + 2,
        color: colors[Math.floor(Math.random() * colors.length)],
        size: Math.random() * 8 + 4
      });
    }

    let animationId: number;
    const animate = () => {
      ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);
      
      particles.forEach((p, index) => {
        ctx.fillStyle = p.color;
        ctx.fillRect(p.x, p.y, p.size, p.size);
        
        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.1; // gravity
        
        // Reset particle if it goes off screen
        if (p.y > window.innerHeight) {
          particles[index] = {
            x: Math.random() * window.innerWidth,
            y: -20,
            vx: (Math.random() - 0.5) * 3,
            vy: Math.random() * 3 + 2,
            color: colors[Math.floor(Math.random() * colors.length)],
            size: Math.random() * 8 + 4
          };
        }
      });
      
      animationId = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      window.removeEventListener('resize', resize);
      if (animationId) cancelAnimationFrame(animationId);
    };
  }, [isVisible, showConfetti]);

  // Safety checks - AFTER all hooks are called
  if (!hasValidScores) {
    console.error('VictoryOverlay: scores prop is missing or incomplete', { scores });
    return null;
  }

  // Allow rendering if we have either players OR roomScores (can build player list from roomScores)
  const hasRoomScores = roomScores && (
    (Array.isArray(roomScores) && roomScores.length > 0) ||
    (typeof roomScores === 'object' && Object.keys(roomScores).length > 0)
  );
  
  if (!hasValidPlayers && !hasRoomScores) {
    console.error('VictoryOverlay: both players and roomScores are missing/empty', { players, roomScores });
    return null;
  }

  if (!isVisible) return null;

  return (
    <>
      <style>{`
        @keyframes victoryPulse {
          0%, 100% { transform: scale(1) translateZ(0); }
          50% { transform: scale(1.01) translateZ(0); }
        }
      `}</style>
      
      {/* Backdrop */}
      <div className="vo-backdrop" style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.85)',
        backdropFilter: 'blur(4px)',
        zIndex: 9998,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }} />

      {/* Confetti Canvas */}
      <canvas ref={canvasRef} style={{
        position: 'fixed',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 9999
      }} />

      {/* Victory Banner - Mobile Optimized */}
      <div style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        maxWidth: 'min(850px, 98vw)',
        width: 'clamp(94%, 98vw, 850px)',
        maxHeight: '92dvh',
        background: 'linear-gradient(135deg, rgba(17, 24, 39, 0.98) 0%, rgba(31, 41, 55, 0.98) 50%, rgba(17, 24, 39, 0.98) 100%)',
        borderRadius: '16px',
        border: winnerIsBidding 
          ? '3px solid rgba(34, 197, 94, 0.6)' 
          : '3px solid rgba(239, 68, 68, 0.6)',
        boxShadow: winnerIsBidding 
          ? '0 0 40px rgba(34, 197, 94, 0.4), 0 8px 32px rgba(0, 0, 0, 0.5)' 
          : '0 0 40px rgba(239, 68, 68, 0.4), 0 8px 32px rgba(0, 0, 0, 0.5)',
        backdropFilter: 'blur(20px)',
        padding: '8px',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 10000
      }}>
        
        {/* Header: Victory Title Only */}
        <div style={{
          fontSize: 'clamp(1rem, 3.2vw, 1.8rem)',
          fontWeight: '900',
          marginBottom: '6px',
          padding: '8px 12px',
          background: winnerIsBidding 
            ? 'linear-gradient(135deg, rgba(34, 197, 94, 0.4), rgba(16, 185, 129, 0.4), rgba(5, 150, 105, 0.4))'
            : 'linear-gradient(135deg, rgba(239, 68, 68, 0.4), rgba(220, 38, 38, 0.4), rgba(185, 28, 28, 0.4))',
          borderRadius: '12px',
          border: `2px solid ${winnerIsBidding ? '#22c55e' : '#ef4444'}`,
          boxShadow: winnerIsBidding 
            ? '0 0 20px rgba(34, 197, 94, 0.5), 0 0 40px rgba(34, 197, 94, 0.2)' 
            : '0 0 20px rgba(239, 68, 68, 0.5), 0 0 40px rgba(239, 68, 68, 0.2)',
          backdropFilter: 'blur(10px)',
          textAlign: 'center'
        }}>
          <div style={{
            fontSize: 'clamp(1rem, 3vw, 1.6rem)',
            fontWeight: '900',
            background: winnerIsBidding 
              ? 'linear-gradient(45deg, #ffd700, #ffed4e, #ffd700, #f59e0b)'
              : 'linear-gradient(45deg, #ffffff, #f8fafc, #ffffff, #e2e8f0)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            backgroundSize: '200% 100%',
            marginBottom: '4px'
          }}>
            {winnerIsBidding ? '🏆 VICTORY! 🏆' : '💥 DEFEAT! 💥'}
          </div>
          <div style={{
            fontSize: 'clamp(0.8rem, 2vw, 1.2rem)',
            fontWeight: '800',
            color: '#ffffff'
          }}>
            {winnerIsBidding ? '🎯 Bidding • Bid Success!' : '🛡️ Opposing • Bid Failed!'}
          </div>
        </div>

        {/* Main Content: Side-by-side Layout */}
        <div style={{
          display: 'flex',
          gap: '6px',
          marginBottom: '6px',
          flex: 1,
          minHeight: 0
        }}>
          
          {/* Left: Team Scores (35% width) - Stacked Vertically */}
          <div style={{
            flex: '0 0 35%',
            display: 'flex',
            flexDirection: 'column',
            gap: '4px'
          }}>
            {/* Bidding Team Score */}
            <div style={{
              background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.15), rgba(16, 185, 129, 0.15))',
              border: winnerIsBidding ? '2px solid #22c55e' : '1px solid rgba(34, 197, 94, 0.4)',
              borderRadius: '8px',
              padding: '6px',
              boxShadow: winnerIsBidding 
                ? '0 0 15px rgba(34, 197, 94, 0.4)' 
                : '0 2px 8px rgba(0, 0, 0, 0.3)'
            }}>
              <div style={{
                fontSize: 'clamp(0.65rem, 1.4vw, 0.8rem)',
                fontWeight: 'bold',
                color: '#22c55e',
                marginBottom: '2px',
                textAlign: 'center'
              }}>
                🎯 BIDDING
              </div>
              <div style={{
                fontSize: 'clamp(1.2rem, 3vw, 2rem)',
                fontWeight: '900',
                color: '#ffffff',
                textAlign: 'center',
                textShadow: '0 0 10px rgba(34, 197, 94, 0.8)',
                marginBottom: '4px'
              }}>
                {scores.biddingTeam}
              </div>
              {/* Individual player scores for bidding team - HORIZONTAL */}
              {(() => {
                // IMPORTANT: Use players array from Colyseus (has correct session IDs for matching with bidderId)
                // Only fall back to roomScores if players array is truly empty
                let biddingPlayers = players.filter(p => p.team === 'bidding');
                
                // If players don't have team assignments, try roomScores as fallback
                // BUT this will cause ID mismatch issues with bidderId!
                if (biddingPlayers.length === 0 && players.length === 0 && roomScores && 'scores' in roomScores && Array.isArray(roomScores.scores)) {
                  // Show first half of players (assuming 2 teams)
                  const halfPoint = Math.ceil(roomScores.scores.length / 2);
                  biddingPlayers = roomScores.scores.slice(0, halfPoint).map((s: any) => ({
                    id: s.playerId,
                    username: s.playerName || s.username,
                    team: 'bidding' as const,
                    isReady: false,
                    isAI: false
                  }));
                } else if (biddingPlayers.length === 0 && players.length > 0) {
                  // Players exist but no team assignment - assume first half is bidding team
                  const halfPoint = Math.ceil(players.length / 2);
                  biddingPlayers = players.slice(0, halfPoint).map(p => ({
                    ...p,
                    team: 'bidding' as const
                  }));
                }
                
                if (biddingPlayers.length === 0) {
                  return null;
                }
                
                console.log('💰 Bidding team calculation:', { 
                  bidAmount, 
                  bidderId, 
                  winnerTeam,
                  playerCount: biddingPlayers.length,
                  playerIds: biddingPlayers.map(p => ({ id: p.id, name: p.username }))
                });
                
                return (
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '3px',
                    fontSize: 'clamp(0.5rem, 1.1vw, 0.65rem)',
                    color: '#d1d5db',
                    justifyContent: 'center'
                  }}>
                    {biddingPlayers.map((p) => {
                      let playerScore: number;
                      
                      // If we have bid information, calculate based on rules
                      if (bidAmount && bidderId) {
                        const bidSuccessful = winnerTeam === 'bidding';
                        const isBidder = p.id === bidderId;
                        
                        console.log(`🎯 Checking player ${p.username} (${p.id}) vs bidderId (${bidderId}): ${isBidder ? 'IS BIDDER ✓' : 'is teammate'}`);
                        
                        if (bidSuccessful) {
                          // Bid successful: Bidder gets 2×bid, teammates get 1×bid
                          playerScore = isBidder ? (2 * bidAmount) : bidAmount;
                        } else {
                          // Bid failed: Bidder gets -1×bid, teammates get 0
                          playerScore = isBidder ? (-1 * bidAmount) : 0;
                        }
                      } else {
                        // Fallback: split team score equally
                        playerScore = Math.floor(scores.biddingTeam / biddingPlayers.length);
                      }
                      
                      return (
                        <span key={p.id} style={{ 
                          whiteSpace: 'nowrap',
                          background: 'rgba(255, 255, 255, 0.05)',
                          padding: '1px 4px',
                          borderRadius: '3px'
                        }}>
                          {p.username}: {playerScore}
                        </span>
                      );
                    })}
                  </div>
                );
              })()}
            </div>

            {/* Opposing Team Score */}
            <div style={{
              background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.15), rgba(220, 38, 38, 0.15))',
              border: !winnerIsBidding ? '2px solid #ef4444' : '1px solid rgba(239, 68, 68, 0.4)',
              borderRadius: '8px',
              padding: '6px',
              boxShadow: !winnerIsBidding 
                ? '0 0 15px rgba(239, 68, 68, 0.4)' 
                : '0 2px 8px rgba(0, 0, 0, 0.3)'
            }}>
              <div style={{
                fontSize: 'clamp(0.65rem, 1.4vw, 0.8rem)',
                fontWeight: 'bold',
                color: '#ef4444',
                marginBottom: '2px',
                textAlign: 'center'
              }}>
                🛡️ OPPOSING
              </div>
              <div style={{
                fontSize: 'clamp(1.2rem, 3vw, 2rem)',
                fontWeight: '900',
                color: '#ffffff',
                textAlign: 'center',
                textShadow: '0 0 10px rgba(239, 68, 68, 0.8)',
                marginBottom: '4px'
              }}>
                {scores.opposingTeam}
              </div>
              {/* Individual player scores for opposing team - HORIZONTAL */}
              {(() => {
                // IMPORTANT: Use players array from Colyseus (has correct session IDs)
                let opposingPlayers = players.filter(p => p.team === 'opposing');
                
                // If players don't have team assignments, try roomScores as fallback
                if (opposingPlayers.length === 0 && players.length === 0 && roomScores && 'scores' in roomScores && Array.isArray(roomScores.scores)) {
                  const allPlayerData = roomScores.scores;
                  
                  // Show second half of players (assuming 2 teams)
                  const halfPoint = Math.ceil(allPlayerData.length / 2);
                  opposingPlayers = allPlayerData.slice(halfPoint).map((s: any) => ({
                    id: s.playerId,
                    username: s.playerName || s.username,
                    team: 'opposing' as const,
                    isReady: false,
                    isAI: false
                  }));
                } else if (opposingPlayers.length === 0 && players.length > 0) {
                  // Players exist but no team assignment - assume second half is opposing team
                  const halfPoint = Math.ceil(players.length / 2);
                  opposingPlayers = players.slice(halfPoint).map(p => ({
                    ...p,
                    team: 'opposing' as const
                  }));
                }
                
                if (opposingPlayers.length === 0) {
                  return null;
                }
                
                console.log('💰 Opposing team calculation:', { 
                  bidAmount, 
                  bidderId, 
                  winnerTeam,
                  playerCount: opposingPlayers.length 
                });
                
                return (
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '3px',
                    fontSize: 'clamp(0.5rem, 1.1vw, 0.65rem)',
                    color: '#d1d5db',
                    justifyContent: 'center'
                  }}>
                    {opposingPlayers.map((p) => {
                      let playerScore: number;
                      
                      // If we have bid information, calculate based on rules
                      if (bidAmount && bidderId) {
                        const bidSuccessful = winnerTeam === 'bidding';
                        // Opposing team: Gets 0 if bid successful, gets bid amount if bid failed
                        playerScore = bidSuccessful ? 0 : bidAmount;
                      } else {
                        // Fallback: split team score equally
                        playerScore = Math.floor(scores.opposingTeam / opposingPlayers.length);
                      }
                      
                      return (
                        <span key={p.id} style={{ 
                          whiteSpace: 'nowrap',
                          background: 'rgba(255, 255, 255, 0.05)',
                          padding: '1px 4px',
                          borderRadius: '3px'
                        }}>
                          {p.username}: {playerScore}
                        </span>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Right: Player Leaderboard (65% width) */}
          <div style={{
            flex: '1',
            background: 'rgba(0, 0, 0, 0.3)',
            borderRadius: '10px',
            padding: '8px',
            overflow: 'auto'
          }}>
            <div style={{
              fontSize: 'clamp(0.75rem, 1.6vw, 0.9rem)',
              fontWeight: 'bold',
              color: '#fbbf24',
              marginBottom: '6px',
              textAlign: 'center',
              textShadow: '0 0 8px rgba(251, 191, 36, 0.8)'
            }}>
              👑 LEADERBOARD
            </div>

            {/* Compact Box Grid - 2 Rows, Rank-wise Pairing (1,4 / 2,5 / 3,6) */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '3px'
            }}>
              {(() => {
                // Extract scores array from roomScores object
                let scoresArray: any[] = [];
                
                // roomScores is an object with a 'scores' property that is an array
                if (roomScores && typeof roomScores === 'object') {
                  if (Array.isArray(roomScores)) {
                    // If roomScores is directly an array (shouldn't happen based on type)
                    scoresArray = roomScores;
                    console.log('📊 roomScores is array (unexpected):', scoresArray);
                  } else if ('scores' in roomScores && Array.isArray(roomScores.scores)) {
                    // roomScores is an object with scores array (expected)
                    scoresArray = roomScores.scores;
                    console.log('📊 roomScores.scores array found:', scoresArray);
                  } else {
                    // Legacy format: roomScores is object, convert entries to array
                    scoresArray = Object.entries(roomScores).map(([playerId, scoreData]: [string, any]) => ({
                      ...scoreData,
                      playerId
                    }));
                    console.log('📊 roomScores is object (legacy), converted:', scoresArray);
                  }
                }
                
                if (scoresArray.length === 0) {
                  console.warn('⚠️ No scores data available');
                  return (
                    <div style={{ 
                      gridColumn: '1 / -1',
                      textAlign: 'center', 
                      color: '#9ca3af', 
                      fontSize: 'clamp(0.6rem, 1.3vw, 0.75rem)', 
                      padding: '8px' 
                    }}>
                      No player data available
                    </div>
                  );
                }
                
                // Sort by cumulative score descending
                const sortedScores = scoresArray
                  .map((scoreData: any) => {
                    // Get player name and data from roomScores
                    const playerName = scoreData.playerName || scoreData.username || scoreData.playerId || 'Unknown';
                    const playerId = scoreData.playerId || scoreData.id || 'unknown';
                    
                    console.log('🎮 Player row:', { 
                      playerId, 
                      playerName, 
                      cumulativeScore: scoreData.cumulativeScore,
                      gamesWon: scoreData.gamesWon, 
                      gamesPlayed: scoreData.gamesPlayed 
                    });
                    
                    return {
                      playerId,
                      playerName,
                      cumulativeScore: scoreData.cumulativeScore || 0,
                      gamesWon: scoreData.gamesWon || 0,
                      gamesPlayed: scoreData.gamesPlayed || 0,
                      isReady: scoreData.isReady || false
                    };
                  })
                  .sort((a, b) => b.cumulativeScore - a.cumulativeScore);

                // Create rank-wise pairing: 1,4 / 2,5 / 3,6
                const gridItems: any[] = [];
                const playerCount = sortedScores.length;
                const halfPoint = Math.ceil(playerCount / 2);
                
                for (let i = 0; i < halfPoint; i++) {
                  // Left column: 1, 2, 3 (ranks 1-3)
                  gridItems.push(sortedScores[i]);
                  
                  // Right column: 4, 5, 6 (ranks 4-6)
                  const rightIndex = i + halfPoint;
                  if (rightIndex < playerCount) {
                    gridItems.push(sortedScores[rightIndex]);
                  }
                }

                return gridItems.map((playerData) => {
                  const actualRank = sortedScores.findIndex(p => p.playerId === playerData.playerId) + 1;
                  const isTopPlayer = actualRank === 1;

                  return (
                    <div
                      key={playerData.playerId}
                      style={{
                        background: isTopPlayer 
                          ? 'rgba(251, 191, 36, 0.2)' 
                          : 'rgba(255, 255, 255, 0.05)',
                        border: isTopPlayer ? '2px solid #fbbf24' : '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '5px',
                        padding: '4px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '2px'
                      }}
                    >
                      {/* Row 1: Rank + Player Name */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '3px'
                      }}>
                        <div style={{
                          fontSize: 'clamp(0.65rem, 1.4vw, 0.8rem)',
                          fontWeight: 'bold',
                          color: isTopPlayer ? '#fbbf24' : '#ffffff',
                          textShadow: isTopPlayer ? '0 0 8px rgba(251, 191, 36, 0.8)' : 'none',
                          minWidth: '20px',
                          textAlign: 'center'
                        }}>
                          {actualRank === 1 ? '🥇' : actualRank === 2 ? '🥈' : actualRank === 3 ? '🥉' : actualRank}
                        </div>
                        <div style={{
                          fontSize: 'clamp(0.6rem, 1.3vw, 0.75rem)',
                          fontWeight: 'bold',
                          color: '#ffffff',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          flex: 1,
                          textShadow: '0 0 8px rgba(0, 0, 0, 0.9)'
                        }}>
                          {playerData.playerName}
                        </div>
                      </div>

                      {/* Row 2: Stats (S:250 W:1 P:1) */}
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-around',
                        fontSize: 'clamp(0.55rem, 1.2vw, 0.7rem)',
                        fontWeight: 'bold',
                        gap: '2px'
                      }}>
                        <span style={{
                          color: '#fbbf24',
                          textShadow: '0 0 4px rgba(251, 191, 36, 0.6)'
                        }}>
                          S:{playerData.cumulativeScore}
                        </span>
                        <span style={{
                          color: '#22c55e',
                          textShadow: '0 0 4px rgba(34, 197, 94, 0.6)'
                        }}>
                          W:{playerData.gamesWon}
                        </span>
                        <span style={{
                          color: '#10b981',
                          textShadow: '0 0 4px rgba(16, 185, 129, 0.6)'
                        }}>
                          P:{playerData.gamesPlayed}
                        </span>
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
            
            {/* All Players Ready Indicator */}
            {(() => {
              const humanPlayers = players.filter(p => !p.isAI);
              const readyHumans = humanPlayers.filter(p => p.isReady);
              const allHumansReady = humanPlayers.length > 0 && readyHumans.length === humanPlayers.length;
              
              if (allHumansReady) {
                return (
                  <div style={{
                    textAlign: 'center',
                    padding: '4px 6px',
                    background: 'rgba(34, 197, 94, 0.2)',
                    borderRadius: '6px',
                    border: '2px solid #22c55e',
                    marginTop: '4px'
                  }}>
                    <div style={{
                      fontSize: 'clamp(0.65rem, 1.4vw, 0.8rem)',
                      color: '#ffffff',
                      fontWeight: 'bold',
                      textShadow: '0 0 8px rgba(34, 197, 94, 0.9)'
                    }}>
                      🎉 All Ready!
                    </div>
                  </div>
                );
              }
              
              return (
                <div style={{
                  textAlign: 'center',
                  fontSize: 'clamp(0.6rem, 1.3vw, 0.7rem)',
                  color: '#d1d5db',
                  fontStyle: 'italic',
                  marginTop: '4px'
                }}>
                  Waiting {humanPlayers.length - readyHumans.length} more...
                </div>
              );
            })()}
          </div>
        </div>

        {/* Bottom: All 3 Buttons in One Row */}
        <div style={{
          display: 'flex',
          gap: '6px',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          {/* Ready/Not Ready Button */}
          {onToggleReady && currentPlayer && (
            <button
              onClick={onToggleReady}
              style={{
                flex: '1',
                background: currentPlayer.isReady 
                  ? 'linear-gradient(135deg, #ef4444, #dc2626)' 
                  : 'linear-gradient(135deg, #10b981, #059669)',
                color: 'white',
                border: 'none',
                padding: '6px 12px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: 'bold',
                fontSize: 'clamp(0.7rem, 1.6vw, 0.85rem)',
                transition: 'all 0.2s',
                boxShadow: currentPlayer.isReady 
                  ? '0 2px 8px rgba(239, 68, 68, 0.3)' 
                  : '0 2px 8px rgba(16, 185, 129, 0.3)',
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = currentPlayer.isReady 
                  ? '0 4px 12px rgba(239, 68, 68, 0.4)' 
                  : '0 4px 12px rgba(16, 185, 129, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = currentPlayer.isReady 
                  ? '0 2px 8px rgba(239, 68, 68, 0.3)' 
                  : '0 2px 8px rgba(16, 185, 129, 0.3)';
              }}
            >
              {currentPlayer.isReady ? '❌ Not Ready' : '✅ Ready'}
            </button>
          )}
          
          {/* Start New Game Button (only shows when all ready) */}
          {(() => {
            const humanPlayers = players.filter(p => !p.isAI);
            const readyHumans = humanPlayers.filter(p => p.isReady);
            const allHumansReady = humanPlayers.length > 0 && readyHumans.length === humanPlayers.length;
            
            if (allHumansReady && onStartNewGame) {
              return (
                <button
                  onClick={onStartNewGame}
                  style={{
                    flex: '1',
                    background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                    color: 'white',
                    border: 'none',
                    padding: '6px 12px',
                    borderRadius: '6px',
                    fontSize: 'clamp(0.7rem, 1.6vw, 0.85rem)',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 2px 8px rgba(34, 197, 94, 0.3)',
                    whiteSpace: 'nowrap'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(34, 197, 94, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(34, 197, 94, 0.3)';
                  }}
                >
                  🚀 Start New Game
                </button>
              );
            }
            return null;
          })()}
          
          {/* Lobby Button */}
          {onReturnToLobby && (
            <button
              onClick={onReturnToLobby}
              style={{
                flex: '1',
                background: 'linear-gradient(135deg, #6b7280, #4b5563)',
                color: 'white',
                border: 'none',
                padding: '6px 12px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: 'bold',
                fontSize: 'clamp(0.7rem, 1.6vw, 0.85rem)',
                transition: 'all 0.2s',
                boxShadow: '0 2px 8px rgba(107, 114, 128, 0.3)',
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(107, 114, 128, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(107, 114, 128, 0.3)';
              }}
            >
              🏠 Lobby
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default VictoryOverlay;
